import type {FC} from 'react'
import {Chip, Tooltip} from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

import {useCustomTranslation} from '@hooks/useCustomTranslation'
import type {DocumentVisibility} from '@interfaces/alpinist/document'

/**
 * One icon per scope, exported so the chat's own scope control wears the same
 * ones. Two places drawing "personal" differently is how a user ends up unsure
 * whether the file in the chat is the row they are looking at in the list.
 */
export const ICON_BY_SCOPE = {
  tenant: BusinessOutlinedIcon,
  personal: LockOutlinedIcon,
  conversation: ChatBubbleOutlineIcon,
} as const

interface Props {
  scope: DocumentVisibility
  /** Appended to the tooltip — the expiry, for a conversation-scoped document. */
  detail?: string
  /** Off inside a select's option list, where MUI owns the hover behaviour. */
  withTooltip?: boolean
}

/**
 * Who can read a document, as one chip.
 *
 * Shared between the Visibility column and the scope filter's options on
 * purpose: the filter offers the same three values the column displays, and two
 * copies of "lock icon plus Only me" is how the list and the control that
 * narrows it end up disagreeing about what a scope looks like.
 */
const DocumentScopeChip: FC<Props> = ({scope, detail, withTooltip = true}) => {
  const [t] = useCustomTranslation('alpinist')
  const Icon = ICON_BY_SCOPE[scope] ?? BusinessOutlinedIcon

  const chip = (
    <Chip
      size="small"
      variant="outlined"
      icon={<Icon fontSize="small" />}
      label={t(`kb.scope.${scope}`)}
    />
  )

  if (!withTooltip) return chip

  return (
    <Tooltip title={[t(`kb.scope_hint.${scope}`), detail].filter(Boolean).join(' • ')}>
      {chip}
    </Tooltip>
  )
}

export default DocumentScopeChip
